"use client"

import { useState, useMemo, useCallback, useTransition, useEffect } from "react"
import { type TradeFill } from "@/components/trading/TradeBlotter"
import { type OhlcvBar } from "@/components/trading/MarketChart"
import { getTradeHistorySinceAction } from "@/actions/trading"
import { useRealtimeOrderBook } from "./useRealtimeOrderBook"
import { useRealtimeTrades } from "./useRealtimeTrades"
import { TRADE_ORDER_SIDE } from "@forestsync/db"
import type { OrderBook as OrderBookData, TradeHistoryRow, Market24hStats } from "@/lib/queries"

export const OB_PRECISION_OPTIONS = [{ label: "1", value: "1" }, { label: "0.1", value: "0.1" }, { label: "0.01", value: "0.01" }]
export const ALL_TIMEFRAMES = ["1m", "3m", "5m", "15m", "30m", "1h", "4h", "12h", "1d", "1w", "1M"]
const TF_SECONDS: Record<string, number> = {
  "1m": 60, "3m": 180, "5m": 300, "15m": 900, "30m": 1800,
  "1h": 3600, "4h": 14400, "12h": 43200, "1d": 86400, "1w": 604800, "1M": 2592000,
}
export type ChartType = "heatmap" | "candlestick" | "line"

// Always fetch the full trade history — intervals are display-only, not a data window.
// 5-year lookback covers any project on this platform.
function timeframeToSince(_tf: string): Date {
  return new Date(Date.now() - 5 * 365 * 86400e3)
}

interface UseMarketChartStateArgs {
  creditType: string
  vintage: number
  projectRefId: string
  initialOrderBook: OrderBookData
  initialTradeHistory: TradeHistoryRow[]
  initialPrice: number | null
  initialChange: number | null
  stats24h?: Market24hStats | null
}

/**
 * Shared read-side state for both TradingTerminal and ObserverTerminal: chart/order-book
 * data, realtime subscriptions, OHLCV bucketing, and VWAP — everything neither mutation UI
 * nor account type changes. Kept in one place so the two terminals can't silently drift
 * (e.g. a VWAP calc fix landing in one but not the other).
 */
export function useMarketChartState({
  creditType,
  vintage,
  projectRefId,
  initialOrderBook,
  initialTradeHistory,
  initialPrice,
  initialChange,
  stats24h,
}: UseMarketChartStateArgs) {
  const initialFills: TradeFill[] = initialTradeHistory
    .filter((r) => r.filledAt !== null)
    .map((r) => ({
      id: r.refId,
      time: new Date(r.filledAt!),
      side: r.side,
      price: Number(r.price ?? 0),
      volume: Number(r.volume),
      transactionRefId: r.transactionRefId ?? undefined,
      aggressorSide: r.aggressorSide ?? null,
      optionContractId: r.optionContractId ?? null,
    }))
    .reverse() // query returns desc(filledAt); reverse to oldest-first to match handleTimeframeChange

  const [obPrecision, setObPrecision] = useState(1)
  const [chartType, setChartType] = useState<ChartType>("heatmap")
  const [timeframe, setTimeframe] = useState("1h")
  const [showVwap, setShowVwap] = useState(true)
  const [showVolumeProfile, setShowVolumeProfile] = useState(false)
  const [visibleTimeframes, setVisibleTimeframes] = useState<string[]>(() => {
    if (typeof window === "undefined") return ["15m", "1h", "1d"]
    try {
      const stored = localStorage.getItem("chart-visible-timeframes")
      if (stored) return JSON.parse(stored)
    } catch { /**/ }
    return ["15m", "1h", "1d"]
  })
  const [timeframePending, startTimeframeTransition] = useTransition()
  const [fillsLoading, setFillsLoading] = useState(false)
  const [chartFills, setChartFills] = useState<TradeFill[]>(initialFills)
  const [hoveredOhlcv, setHoveredOhlcv] = useState<{ time: number; open: number; high: number; low: number; close: number; volume: number } | null>(null)

  const orderBook = useRealtimeOrderBook(creditType, vintage, projectRefId, initialOrderBook)
  const [recentFills, setRecentFills] = useState<TradeFill[]>(initialFills)
  const [livePrice, setLivePrice] = useState<number | null>(initialPrice)
  const [liveStats, setLiveStats] = useState<typeof stats24h>(stats24h ?? null)
  const [liveChange, setLiveChange] = useState<number | null>(initialChange)
  // Stable 24h-ago reference — used to recalculate change % as new fills arrive
  const price24hAgo = useMemo(
    () => (initialPrice !== null && initialChange !== null && initialChange !== 0)
      ? initialPrice / (1 + initialChange / 100)
      : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const priceUp = livePrice !== null && initialPrice !== null && livePrice >= initialPrice

  const realtimeNewFills = useRealtimeTrades(creditType, vintage, projectRefId, initialFills)
  useEffect(() => {
    if (realtimeNewFills.length === 0) return
    setChartFills((prev) => {
      const existingIds = new Set(prev.map((f) => f.id))
      const unique = realtimeNewFills.filter((f) => !existingIds.has(f.id))
      return unique.length > 0 ? [...prev, ...unique] : prev
    })
    setRecentFills((prev) => {
      const existingIds = new Set(prev.map((f) => f.id))
      const unique = realtimeNewFills.filter((f) => !existingIds.has(f.id))
      return unique.length > 0 ? [...[...unique].reverse(), ...prev].slice(0, 50) : prev
    })
    // Drive price, 24h stats, and change % from new fills
    const sorted = [...realtimeNewFills].sort((a, b) => b.time.getTime() - a.time.getTime())
    const latest = sorted[0]
    if (latest) {
      setLivePrice(latest.price)
      if (price24hAgo !== null) setLiveChange(((latest.price - price24hAgo) / price24hAgo) * 100)
      setLiveStats((prev) => {
        const base = prev ?? { high: null, low: null, volumeTco2e: 0, volumeEur: 0 }
        const prices = realtimeNewFills.map((f) => f.price)
        return {
          high: base.high !== null ? Math.max(base.high, ...prices) : Math.max(...prices),
          low: base.low !== null ? Math.min(base.low, ...prices) : Math.min(...prices),
          volumeTco2e: base.volumeTco2e + realtimeNewFills.reduce((s, f) => s + f.volume, 0),
          volumeEur: base.volumeEur + realtimeNewFills.reduce((s, f) => s + f.price * f.volume, 0),
        }
      })
    }
  }, [realtimeNewFills, price24hAgo])

  // Build OHLCV bars from fills for candlestick/line chart types
  const ohlcvBars = useMemo((): OhlcvBar[] => {
    const bucketSecs = TF_SECONDS[timeframe] ?? 3600
    const map = new Map<number, OhlcvBar & { bidVol: number; askVol: number }>()
    const sorted = [...chartFills].sort((a, b) => a.time.getTime() - b.time.getTime())
    for (const f of sorted) {
      const bucket = Math.floor(f.time.getTime() / 1000 / bucketSecs) * bucketSecs
      const time = bucket as OhlcvBar["time"]
      const existing = map.get(bucket)
      const isBid = f.aggressorSide === TRADE_ORDER_SIDE.BID
      if (!existing) {
        map.set(bucket, { time, open: f.price, high: f.price, low: f.price, close: f.price, volume: f.volume, bidVol: isBid ? f.volume : 0, askVol: isBid ? 0 : f.volume })
      } else {
        existing.high = Math.max(existing.high, f.price)
        existing.low = Math.min(existing.low, f.price)
        existing.close = f.price
        existing.volume = (existing.volume ?? 0) + f.volume
        if (isBid) existing.bidVol += f.volume; else existing.askVol += f.volume
      }
    }
    return [...map.values()]
      .sort((a, b) => (a.time as number) - (b.time as number))
      .map(({ bidVol, askVol, ...bar }) => ({
        ...bar,
        volumeColor: bidVol >= askVol ? "var(--mantine-color-green-5)" : "var(--mantine-color-red-5)",
      }))
  }, [chartFills, timeframe])

  // Broker option exercise block trades ("volume bubbles"), bucketed onto the same candle the fill landed in
  const blockTradeMarkers = useMemo((): { time: OhlcvBar["time"]; text: string }[] => {
    const bucketSecs = TF_SECONDS[timeframe] ?? 3600
    const byBucket = new Map<number, number>()
    for (const f of chartFills) {
      if (f.optionContractId == null) continue
      const bucket = Math.floor(f.time.getTime() / 1000 / bucketSecs) * bucketSecs
      byBucket.set(bucket, (byBucket.get(bucket) ?? 0) + f.volume)
    }
    return [...byBucket.entries()]
      .sort(([a], [b]) => a - b)
      .map(([bucket, volume]) => ({ time: bucket as OhlcvBar["time"], text: `Block ${volume}t` }))
  }, [chartFills, timeframe])

  // Cumulative VWAP line across all fills (session-level, not per-candle)
  const vwapOverlay = useMemo((): { time: OhlcvBar["time"]; value: number }[] => {
    let cumPxVol = 0
    let cumVol = 0
    const points: { time: OhlcvBar["time"]; value: number }[] = []
    const sorted = [...chartFills].sort((a, b) => a.time.getTime() - b.time.getTime())
    for (const f of sorted) {
      cumPxVol += f.price * f.volume
      cumVol += f.volume
      points.push({ time: Math.floor(f.time.getTime() / 1000) as OhlcvBar["time"], value: cumPxVol / cumVol })
    }
    return points
  }, [chartFills])

  function handleOrderFilled(fill: TradeFill) {
    setRecentFills((prev) => [fill, ...prev.slice(0, 49)])
    setChartFills((prev) => [fill, ...prev])
    setLivePrice(fill.price)
    if (price24hAgo !== null) setLiveChange(((fill.price - price24hAgo) / price24hAgo) * 100)
    setLiveStats((prev) => {
      const base = prev ?? { high: null, low: null, volumeTco2e: 0, volumeEur: 0 }
      return {
        high: base.high !== null ? Math.max(base.high, fill.price) : fill.price,
        low: base.low !== null ? Math.min(base.low, fill.price) : fill.price,
        volumeTco2e: base.volumeTco2e + fill.volume,
        volumeEur: base.volumeEur + fill.price * fill.volume,
      }
    })
  }

  async function handleTimeframeChange(tf: string) {
    setFillsLoading(true)
    try {
      const rows = await getTradeHistorySinceAction(
        creditType, vintage, projectRefId, timeframeToSince(tf).toISOString()
      )
      startTimeframeTransition(() => {
        setTimeframe(tf)
        setChartFills(
          rows
            .filter((r) => r.filledAt !== null)
            .map((r) => ({
              id: r.refId,
              time: new Date(r.filledAt!),
              side: r.side,
              price: Number(r.price ?? 0),
              volume: Number(r.volume),
              aggressorSide: r.aggressorSide ?? null,
            }))
            .reverse()
        )
      })
    } finally {
      setFillsLoading(false)
    }
  }

  const handleVisibleTimeframesChange = useCallback((tf: string, checked: boolean) => {
    setVisibleTimeframes((prev) => {
      const next = checked ? [...prev, tf].filter((v) => ALL_TIMEFRAMES.includes(v)) : prev.filter((v) => v !== tf)
      const safe = next.length > 0 ? next : prev
      localStorage.setItem("chart-visible-timeframes", JSON.stringify(safe))
      return safe
    })
  }, [])

  return {
    obPrecision, setObPrecision,
    chartType, setChartType,
    timeframe, timeframePending,
    showVwap, setShowVwap,
    showVolumeProfile, setShowVolumeProfile,
    visibleTimeframes,
    fillsLoading,
    chartFills,
    hoveredOhlcv, setHoveredOhlcv,
    orderBook,
    recentFills,
    livePrice,
    liveStats,
    liveChange,
    priceUp,
    ohlcvBars,
    blockTradeMarkers,
    vwapOverlay,
    handleOrderFilled,
    handleTimeframeChange,
    handleVisibleTimeframesChange,
  }
}
